import React, { ReactElement } from 'react';
import type { ElementRef } from 'react';

import { styled } from '@backyard-ui/styles';

import { Button, ButtonProps } from '.';
import { ButtonEl } from './Button';
import { Icon } from '../Icon';
import { Spinner } from '../Spinner';

export interface IconButtonProps
  extends Omit<ButtonProps, 'iconLeft' | 'iconRight' | 'children'> {
  /**
   * A11y: A label that describes the button
   */
  ariaLabel?: string;

  /**
   * The icon to be used in the button.
   */
  icon: ReactElement;
}

const ButtonIconEl = styled(ButtonEl, {
  width: 'auto',

  variants: {
    size: {
      xs: {
        minWidth: '$7',
        height: '$7',
        padding: '$0',
      },
      sm: {
        minWidth: '$10',
        height: '$10',
        padding: '$0',
      },
      md: {
        minWidth: '$12',
        height: '$12',
        padding: '$0',
      },
    },
  },
  defaultVariants: {
    size: 'sm',
  },
});

const IconButton = React.forwardRef<ElementRef<'button'>, IconButtonProps>(
  (
    {
      icon,
      ariaLabel,
      size = 'sm',
      isDisabled = false,
      isLoading = false,
      ...props
    },
    ref
  ) => {
    return (
      <ButtonIconEl
        ref={ref}
        aria-label={ariaLabel}
        size={size}
        disabled={isDisabled || isLoading}
        {...props}
      >
        <div className="Button-icon ">
          {isLoading ? <Spinner size={size} /> : <Icon>{icon}</Icon>}
        </div>
      </ButtonIconEl>
    );
  }
);

Button.displayName = 'IconButton';

export default IconButton;
